import { mediaQueriesBP } from "@bevco/Utilities/variables";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import {
    getProductListDataView,
    getProductListDataViewProperty,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductListFiltersAccordionSection, {
    ProductListFilterAccordionSectionStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListFilterAccordionSection";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    expandByDefault = "expandByDefault",
}

interface OwnProps extends WidgetProps {
    facets: FacetModel[];
    extendedStyles?: RefineResultsFilterStyles;
    fields: {
        [fields.expandByDefault]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const {
        pages: {
            productList: { productFilters },
        },
    } = state;

    const facets = [
        {
            id: "stocked",
            name: translate("Stocked Items Only"),
            count: -1,
            selected: productFilters.stockedItemsOnly ?? false,
        },
    ];

    // Attribute type facet "Product Type" has 2 custom attribute values: "cmi" and "overstock"
    // The following code will add 2 facets to the "Refine Results" filter

    const productTypeFacet = getProductListDataViewProperty(state, "attributeTypeFacets")?.filter(
        o => o.name === "Product Type",
    );

    productTypeFacet?.forEach(o => {
        o.attributeValueFacets?.forEach(p => {
            let label = "";
            if (p.value.toLowerCase() === "cmi") {
                label = `${translate("My Customer Materials")}`;
            }

            if (p.value.toLowerCase() === "overstock outlet") {
                label = `${translate("Overstock Outlet")}`;
            }

            facets.push({
                id: p.attributeValueId,
                name: label ?? p.valueDisplay,
                count: p.count,
                selected: p.selected,
            });
        });
    });

    return {
        loaded: !!getProductListDataView(state).value,
        facets,
        view: state.pages.productList.view,
        productTypeFacet,
    };
};

const mapDispatchToProps = {
    addProductFilters,
    removeProductFilters,
};

export interface RefineResultsFilterStyles extends ProductListFilterAccordionSectionStyles {
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    list?: InjectableCss;
    wrapper?: InjectableCss;
}

export const refineResultsFilterStyles: RefineResultsFilterStyles = {
    wrapper: {
        css: css`
            display: none;
            ${mediaQueriesBP.minMD} {
                display: block;
            }
        `,
    },
    checkBoxGroup: {
        css: css`
            display: inline-block;
            width: 100%;
            min-width: max-content;
        `,
    },
    checkBox: {
        css: css<CheckboxStyleProps>`
            align-items: flex-start;
            span[role="checkbox"] {
                ${({ _sizeVariant }) =>
                    _sizeVariant === "small"
                        ? css`
                              margin-top: 5px;
                          `
                        : css`
                              margin-top: 4px;
                          `}
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
        },
    },
    checkBoxSelected: {
        css: css<CheckboxStyleProps>`
            align-items: flex-start;
            span[role="checkbox"] {
                ${({ _sizeVariant }) =>
                    _sizeVariant === "small"
                        ? css`
                              margin-top: 5px;
                          `
                        : css`
                              margin-top: 4px;
                          `}
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
            weight: "bold",
        },
    },
    list: {
        css: css`
            display: inline-flex;
            flex-direction: row;
            gap: 16px;
        `,
    },
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

const RefineResultsFilter: FC<Props> = ({
    loaded,
    facets,
    extendedStyles,
    removeProductFilters,
    addProductFilters,
    view,
    fields,
}) => {
    // TODO ISC-11787 - make showMoreLimit configurable
    const showMoreLimit = 10;
    const [styles] = React.useState(() => mergeToNew(refineResultsFilterStyles, extendedStyles));

    if (!loaded) {
        return null;
    }

    const onChangeFacet = (facet: FacetModel) => {
        if (facet.id === "stocked") {
            if (facet.selected) {
                removeProductFilters({ stockedItemsOnly: false });
            } else {
                addProductFilters({ stockedItemsOnly: true });
            }
            return;
        }

        if (facet.selected) {
            removeProductFilters({ attributeValueIds: [facet.id] });
        } else {
            addProductFilters({ attributeValueIds: [facet.id] });
        }
    };

    if (view === "List") {
        return (
            <ProductListFiltersAccordionSection
                title={translate("Refine Results Table")}
                facets={facets}
                onChangeFacet={onChangeFacet}
                showMoreLimit={showMoreLimit}
                expandByDefault={fields.expandByDefault}
                extendedStyles={styles}
            />
        );
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography variant="h5">{translate("Refine Results")}</Typography>
            <StyledWrapper as="ul" {...styles.list}>
                {facets?.map(facet => (
                    <CheckboxGroup {...styles.checkBoxGroup} key={facet.id} as="li">
                        <Checkbox
                            {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                            checked={facet.selected}
                            onChange={() => onChangeFacet(facet)}
                            data-test-selector={`facetFilter${facet.id}`}
                        >
                            {facet.name} {facet.count > 0 && `(${facet.count})`}
                        </Checkbox>
                    </CheckboxGroup>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RefineResultsFilter as any),
    definition: {
        group: "Product List",
        displayName: "Refine Results Filter",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.expandByDefault,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                instructions: "Only applies to the list view.",
            },
        ],
    },
};

export default widgetModule;
